import { JSX, useCallback, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import { Theme } from '@emotion/react';

import { DigitalHubChip } from '@dh/components';
import { computeTopicHierarchy, Topic } from '@dh/types';

export interface TopicOption {
    id: string;
    label: string;
    isSelected: boolean;
    secondaryContent?: JSX.Element;
    primaryFontWeight?: number;
}

interface UseTopicClusterResult {
    topicClusters: ReturnType<typeof computeTopicHierarchy>;
    filteredTopics: Topic[];
    handleTopicFilterChange: (selectedParentIds: string[]) => void;
    topicOptions: TopicOption[];
}

export function useTopicCluster(allTopics: Topic[], theme: Theme): UseTopicClusterResult {
    // Manage topics filter state internally
    const [topicsFilter, setTopicsFilter] = useState<Topic[]>([]);

    // Compute topic hierarchy
    const topicClusters = useMemo(() => computeTopicHierarchy(allTopics), [allTopics]);

    const handleTopicFilterChange = useCallback(
        (selectedParentIds: string[]) => {
            const selectedTopics: Topic[] = [];

            selectedParentIds.forEach((parentId) => {
                // Find all topics with this parent
                const cluster = topicClusters.find(
                    (cluster) => cluster.parent.documentId === parentId
                );
                if (cluster) {
                    selectedTopics.push(...cluster.topics);
                }
            });

            setTopicsFilter(selectedTopics);
        },
        [topicClusters]
    );

    const topicOptions = useMemo(() => {
        return topicClusters.map((cluster) => {
            const clusterTopics = cluster.topics;
            const selectedTopicsInCluster = clusterTopics.filter((topic) =>
                topicsFilter.includes(topic)
            );
            const allSelected =
                selectedTopicsInCluster.length === clusterTopics.length && clusterTopics.length > 0;

            // Create badges for child topics with automatic wrapping
            const topicBadges = (
                <Stack
                    direction='row'
                    spacing={0.5}
                    flexWrap='wrap'
                    useFlexGap
                    sx={{ maxWidth: theme.spacing(100) }}
                >
                    {clusterTopics.map((topic) => (
                        <DigitalHubChip key={topic.id} label={topic.title} size='small' />
                    ))}
                </Stack>
            );

            return {
                id: cluster.parent.documentId,
                label: cluster.parent.title,
                isSelected: allSelected,
                secondaryContent: topicBadges,
                primaryFontWeight: 700
            };
        });
    }, [topicClusters, topicsFilter, theme]);

    return {
        topicClusters,
        filteredTopics: topicsFilter,
        handleTopicFilterChange,
        topicOptions
    };
}
